<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container merge-page">
      <header class="page-header">
        <h1>Recupero account duplicati</h1>
        <p class="page-intro">
          Cerca gli utenti con cognome, email o UID, seleziona l&rsquo;account principale e quello da unire,
          quindi scegli i valori finali da mantenere. Al termine dell&rsquo;operazione tutte le referenze
          saranno spostate sull&rsquo;utente principale e l&rsquo;account secondario verrà archiviato.
        </p>
      </header>

      <section class="card search-card">
        <h2>Ricerca utenti</h2>
        <div class="search-grid">
          <label class="search-field">
            <span>Cognome</span>
            <input
              v-model.trim="filters.surname"
              type="text"
              placeholder="Es. Rossi"
              autocomplete="family-name"
              @keyup.enter="searchUsers"
            />
          </label>
          <label class="search-field">
            <span>Email</span>
            <input
              v-model.trim="filters.email"
              type="email"
              placeholder="nome@dominio.it"
              autocomplete="email"
              @keyup.enter="searchUsers"
            />
          </label>
          <label class="search-field">
            <span>UID</span>
            <input
              v-model.trim="filters.uid"
              type="text"
              placeholder="UID Firebase"
              @keyup.enter="searchUsers"
            />
          </label>
          <div class="search-actions">
            <button
              type="button"
              class="btn btn-primary staff-button-neutral"
              :disabled="isSearching"
              @click="searchUsers"
            >
              <span class="material-icons" aria-hidden="true">search</span>
              Cerca
            </button>
            <button
              type="button"
              class="btn btn-ghost"
              :disabled="isSearching || !hasSearched"
              @click="resetSearch"
            >
              Reset
            </button>
          </div>
        </div>
        <p class="search-hint">
          Suggerimento: inserisci solo le prime lettere del cognome per individuare rapidamente possibili duplicati.
        </p>
      </section>

      <section v-if="hasSearched" class="card results-card">
        <div class="card-header">
          <h2>Risultati ricerca</h2>
          <span v-if="isSearching" class="inline-loader">Ricerca in corso…</span>
        </div>

        <div v-if="searchError" class="alert alert-error">
          {{ searchError }}
        </div>

        <div v-else-if="users.length === 0" class="empty-state">
          Nessun utente trovato con i criteri indicati.
        </div>

        <div v-else class="table-wrapper">
          <table class="users-table">
            <thead>
              <tr>
                <th>Primario</th>
                <th>Secondario</th>
                <th>UID</th>
                <th>Nome</th>
                <th>Cognome</th>
                <th>Email</th>
                <th>Studio</th>
                <th>Telefono</th>
                <th>Creato il</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.uid">
                <td class="radio-cell">
                  <input
                    :id="`primary-${user.uid}`"
                    type="radio"
                    name="primary"
                    :value="user.uid"
                    v-model="selectedPrimary"
                    :disabled="isMerging || selectedSecondary === user.uid"
                  />
                </td>
                <td class="radio-cell">
                  <input
                    :id="`secondary-${user.uid}`"
                    type="radio"
                    name="secondary"
                    :value="user.uid"
                    v-model="selectedSecondary"
                    :disabled="isMerging || selectedPrimary === user.uid"
                  />
                </td>
                <td>
                  <label :for="`primary-${user.uid}`" class="uid-label">{{ user.uid }}</label>
                </td>
                <td>{{ user.name || '—' }}</td>
                <td>{{ user.surname || '—' }}</td>
                <td>{{ user.email || '—' }}</td>
                <td>{{ user.studio || '—' }}</td>
                <td>{{ user.phone || '—' }}</td>
                <td>{{ formatDate(user.created_time) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="showPreviewPanel" class="card merge-card">
        <div class="card-header">
          <h2>Confronto e fusione</h2>
          <span v-if="previewLoading" class="inline-loader">Caricamento dati…</span>
        </div>

        <div v-if="previewError" class="alert alert-error">
          {{ previewError }}
        </div>

        <template v-else-if="previewData">
          <div class="merge-summary">
            <article class="user-card">
              <h3>Account principale</h3>
              <p class="user-card__name">
                {{ previewData.primaryUser.name || '—' }} {{ previewData.primaryUser.surname || '—' }}
              </p>
              <dl>
                <div>
                  <dt>UID</dt>
                  <dd>{{ previewData.primaryUser.uid }}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{{ previewData.primaryUser.email || '—' }}</dd>
                </div>
                <div>
                  <dt>Studio</dt>
                  <dd>{{ previewData.primaryUser.studio || '—' }}</dd>
                </div>
                <div>
                  <dt>Telefono</dt>
                  <dd>{{ previewData.primaryUser.phone || '—' }}</dd>
                </div>
              </dl>
            </article>

            <article class="user-card user-card--secondary">
              <h3>Account da unire</h3>
              <p class="user-card__name">
                {{ previewData.secondaryUser.name || '—' }} {{ previewData.secondaryUser.surname || '—' }}
              </p>
              <dl>
                <div>
                  <dt>UID</dt>
                  <dd>{{ previewData.secondaryUser.uid }}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{{ previewData.secondaryUser.email || '—' }}</dd>
                </div>
                <div>
                  <dt>Studio</dt>
                  <dd>{{ previewData.secondaryUser.studio || '—' }}</dd>
                </div>
                <div>
                  <dt>Telefono</dt>
                  <dd>{{ previewData.secondaryUser.phone || '—' }}</dd>
                </div>
              </dl>
            </article>

            <aside class="references-card">
              <h3>Referenze collegate</h3>
              <ul>
                <li v-for="ref in referenceBreakdown" :key="ref.collection">
                  <span class="references-card__collection">{{ ref.collection }}</span>
                  <span class="references-card__count">{{ ref.total }}</span>
                </li>
              </ul>
              <p class="references-card__note">
                Alimentazioni da trasferire:
                <strong>{{ previewData.alimentazioni?.secondaryCount || 0 }}</strong>
              </p>
              <p class="references-card__note">
                Percorsi da trasferire: <strong>{{ previewData.percorsi?.secondaryCount || 0 }}</strong>
              </p>
            </aside>
          </div>

          <div v-if="displayedFields.length" class="merge-table-wrapper">
            <table class="merge-table">
              <thead>
                <tr>
                  <th>Campo</th>
                  <th class="value-col">Valore primario</th>
                  <th class="value-col">Valore secondario</th>
                  <th class="value-col">Valore finale</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="field in displayedFields"
                  :key="field.key"
                  :class="['merge-row', { 'merge-row--note': field.isNote }]"
                >
                  <th scope="row">{{ field.label }}</th>
                  <td>
                    <div class="value-cell">
                      <button
                        type="button"
                        class="btn btn-ghost btn-small"
                        @click="applyField('primary', field.key)"
                        :disabled="isMerging"
                      >
                        Usa
                      </button>
                      <span>{{ formatValue(previewData.primaryUser[field.key]) }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="value-cell">
                      <button
                        type="button"
                        class="btn btn-ghost btn-small"
                        @click="applyField('secondary', field.key)"
                        :disabled="isMerging || previewData.secondaryUser[field.key] === undefined"
                      >
                        Usa
                      </button>
                      <span>{{ formatValue(previewData.secondaryUser[field.key]) }}</span>
                    </div>
                  </td>
                  <td :class="['final-col', { 'final-col--note': field.isNote }]">
                    <div v-if="field.isNote" class="note-actions">
                      <button
                        type="button"
                        class="btn btn-ghost btn-small"
                        :disabled="isMerging"
                        @click="combineNotes(field.key)"
                      >
                        Unisci note
                      </button>
                    </div>
                    <textarea
                      v-if="field.isNote"
                      v-model="finalUser[field.key]"
                      class="final-input final-input--note"
                      :rows="5"
                      :disabled="isMerging"
                      placeholder="Inserisci le note finali"
                    />
                    <select
                      v-else-if="field.component === 'select'"
                      v-model="finalUser[field.key]"
                      class="final-input"
                      :disabled="isMerging"
                    >
                      <option value="">—</option>
                      <option value="true">Sì</option>
                      <option value="false">No</option>
                    </select>
                    <input
                      v-else
                      type="text"
                      v-model="finalUser[field.key]"
                      class="final-input"
                      :disabled="isMerging"
                    />
                    <p v-if="field.isNote" class="note-hint">
                      Puoi combinare e modificare liberamente le note prima di salvare.
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="alert alert-info">
            Nessun campo modificabile rilevato per gli account selezionati.
          </div>

          <div class="merge-options">
            <label class="option-line">
              <input
                type="checkbox"
                v-model="mergeOptions.includeSecondaryEmail"
                :disabled="!previewData.secondaryUser.email"
              />
              Aggiungi l&rsquo;email dell&rsquo;account secondario fra gli alias del principale
            </label>
            <label class="option-line">
              <input
                type="checkbox"
                v-model="mergeOptions.disableSecondaryAuth"
              />
              Disabilita l&rsquo;accesso dell&rsquo;account secondario dopo la fusione
            </label>
            <label class="option-line option-line--stacked">
              <span>Email aggiuntive da salvare come alias (separate da virgola)</span>
              <input
                v-model="mergeOptions.additionalAliases"
                type="text"
                placeholder="email.alternativa@dominio.it"
                :disabled="isMerging"
              />
            </label>
            <label class="option-line option-line--stacked">
              <span>Note operative</span>
              <textarea
                v-model="mergeOptions.note"
                rows="3"
                placeholder="Annotazioni utili per l’audit"
                :disabled="isMerging"
              />
            </label>
          </div>

          <div class="merge-actions">
            <div class="merge-actions__group">
              <button
                type="button"
                class="btn btn-ghost"
                :disabled="isMerging"
                @click="applyAll('primary')"
              >
                Usa tutti i dati primari
              </button>
              <button
                type="button"
                class="btn btn-ghost"
                :disabled="isMerging"
                @click="applyAll('secondary')"
              >
                Usa tutti i dati secondari
              </button>
              <button
                type="button"
                class="btn btn-ghost"
                :disabled="isMerging"
                @click="fillMissingFromSecondary"
              >
                Completa campi vuoti con i dati secondari
              </button>
            </div>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="isMerging"
              @click="executeMerge"
            >
              <span class="material-icons" aria-hidden="true">merge_type</span>
              Unisci gli account
            </button>
          </div>
        </template>
      </section>

      <section v-if="mergeResult" class="card result-card">
        <h3>Operazione completata</h3>
        <p>
          Tutte le referenze sono state spostate sull&rsquo;account
          <strong>{{ mergeResult.primaryUid }}</strong>.
        </p>
        <ul>
          <li v-for="item in mergeResult.referenceUpdates" :key="item.collection">
            {{ item.collection }} → {{
              item.fields.reduce((total, f) => total + f.updated, 0)
            }} documenti aggiornati
          </li>
        </ul>
        <p>
          Programmi alimentazione trasferiti: {{ mergeResult.alimentazioni?.transferredPrograms || 0 }}.
        </p>
        <p>
          Percorsi aggiornati: {{ mergeResult.percorsi?.totalUpdated || 0 }}
          <template v-if="mergeResult.percorsi?.totalUpdated">
            <span>
              (da UID: {{ mergeResult.percorsi?.fromUid || 0 }}
              <span v-if="mergeResult.percorsi?.fromEmail">
                , da email: {{ mergeResult.percorsi?.fromEmail }}
              </span>
              <span v-if="mergeResult.percorsi?.matchedEmails?.length">
                — email coinvolte: {{ mergeResult.percorsi.matchedEmails.join(', ') }}
              </span>
              )
            </span>
          </template>
        </p>
        <p v-if="mergeResult.authUpdate?.disabled">
          L&rsquo;account secondario è stato disabilitato per l&rsquo;accesso.
        </p>
        <p v-if="mergeResult.authUpdate?.error" class="alert alert-warning" role="alert">
          I dati sono stati trasferiti, ma non è stato possibile disabilitare
          l&rsquo;accesso dell&rsquo;account secondario. Verifica l&rsquo;account
          in Firebase Authentication prima di considerare chiusa l&rsquo;operazione.
        </p>
        <p v-if="mergeResult.technicalTickets">
          <template v-if="mergeResult.technicalTickets.merged">
            Ticket tecnico consolidato su
            <strong>{{ mergeResult.technicalTickets.targetTicketId }}</strong>
            ({{ mergeResult.technicalTickets.totalConversations || 0 }} conversazioni,
            eliminati {{ mergeResult.technicalTickets.removedTicketIds?.length || 0 }} duplicati).
          </template>
          <template v-else-if="mergeResult.technicalTickets.existingTickets === 0">
            Nessun ticket tecnico associato all&rsquo;utente unificato.
          </template>
          <template v-else>
            Ticket tecnico <strong>{{ mergeResult.technicalTickets.targetTicketId }}</strong>
            aggiornato ({{ mergeResult.technicalTickets.totalConversations || 0 }} conversazioni).
          </template>
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  nextTick,
} from 'vue';
import {
  getFirestore,
  collection,
  getDocs,
} from 'firebase/firestore';
import {
  getFunctions,
  httpsCallable,
} from 'firebase/functions';
import SidebarComponent from '../SidebarComponent.vue';

const PROTECTED_KEYS = new Set([
  'uid',
  'userId',
  'created_time',
  'createdTime',
  'createdAt',
  'registrationDate',
  'lastLogin',
  'lastLoginAt',
  'mergedAccounts',
  'aliasEmails',
  'password',
  'hash',
  'salt',
]);

const FIELD_LABELS = {
  email: 'Email',
  name: 'Nome',
  surname: 'Cognome',
  studio: 'Studio',
  phone: 'Telefono',
  indirizzo: 'Indirizzo',
  cap: 'CAP',
  provincia: 'Provincia',
  regione: 'Regione',
  luogoNascita: 'Luogo di nascita',
  birthDate: 'Data di nascita',
  cf: 'Codice fiscale',
  note: 'Note',
  canUploadFiles: 'Può caricare documenti',
  gender: 'Genere',
};

const formatKey = (key) =>
  FIELD_LABELS[key] ||
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const parseAliasList = (value = '') =>
  value
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export default {
  name: 'RecuperoAccountDoppi',
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const functions = getFunctions();
    const filters = reactive({
      surname: '',
      email: '',
      uid: '',
    });

    const users = ref([]);
    const isSearching = ref(false);
    const hasSearched = ref(false);
    const searchError = ref('');

    const selectedPrimary = ref(null);
    const selectedSecondary = ref(null);

    const previewLoading = ref(false);
    const previewError = ref('');
    const previewData = ref(null);

    const finalUser = reactive({});
    const mergeOptions = reactive({
      includeSecondaryEmail: true,
      disableSecondaryAuth: true,
      additionalAliases: '',
      note: '',
    });

    const mergeResult = ref(null);
    const isMerging = ref(false);
    let previewGeneration = 0;
    let selectionGeneration = 0;

    const previewFn = httpsCallable(functions, 'previewUserMerge', { timeout: 180000 });
    const mergeFn = httpsCallable(functions, 'mergeUsers', { timeout: 600000 });

    const showPreviewPanel = computed(
      () => !!selectedPrimary.value && !!selectedSecondary.value
    );

    const referenceBreakdown = computed(() => {
      if (!previewData.value?.referenceSummary?.summaries) {
        return [];
      }
      return previewData.value.referenceSummary.summaries.filter(
        (item) => item.total > 0
      );
    });

    const isNoteField = (key = '') => /note/i.test(key);

    const displayedFields = computed(() => {
      if (!previewData.value) return [];

      const primary = previewData.value.primaryUser || {};
      const secondary = previewData.value.secondaryUser || {};

      const keys = Array.from(
        new Set([...Object.keys(primary), ...Object.keys(secondary)])
      ).filter((key) => {
        if (PROTECTED_KEYS.has(key)) return false;
        if (key.startsWith('_')) return false;
        const primaryVal = primary[key];
        const secondaryVal = secondary[key];
        const isObjectLike = (val) =>
          typeof val === 'object' &&
          val !== null &&
          !(val instanceof Date) &&
          !(typeof val?.toDate === 'function');
        if (isObjectLike(primaryVal) || isObjectLike(secondaryVal)) {
          return false;
        }
        return true;
      });

      return keys
        .sort((a, b) => formatKey(a).localeCompare(formatKey(b)))
        .map((key) => {
          const primaryVal = primary[key];
          const secondaryVal = secondary[key];
          const isBooleanField =
            typeof primaryVal === 'boolean' || typeof secondaryVal === 'boolean';
          const noteField = isNoteField(key);
          return {
            key,
            label: formatKey(key),
            component: isBooleanField ? 'select' : noteField ? 'textarea' : 'input',
            isNote: noteField,
          };
        });
    });

    const displayedFieldMap = computed(() => {
      const map = new Map();
      displayedFields.value.forEach((field) => {
        map.set(field.key, field);
      });
      return map;
    });

    const resetPreviewState = ({ preserveResult = false } = {}) => {
      previewGeneration += 1;
      previewLoading.value = false;
      previewData.value = null;
      previewError.value = '';
      if (!preserveResult) mergeResult.value = null;
      Object.keys(finalUser).forEach((key) => {
        delete finalUser[key];
      });
    };

    const normalizeForModel = (value, fieldMeta) => {
      if (!fieldMeta) {
        return value === undefined || value === null ? '' : value;
      }

      if (fieldMeta.component === 'select') {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        if (value === 'true' || value === 'false') return value;
        return String(value);
      }

      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      return String(value);
    };

    const searchUsers = async () => {
      selectionGeneration += 1;
      searchError.value = '';
      isSearching.value = true;
      hasSearched.value = true;
      users.value = [];
      resetPreviewState();
      selectedPrimary.value = null;
      selectedSecondary.value = null;

      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const surnameFilter = filters.surname.toLowerCase();
        const emailFilter = filters.email.toLowerCase();
        const uidFilter = filters.uid.toLowerCase();

        const results = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            uid: doc.id,
          }))
          .filter((user) => {
            let matches = true;

            if (surnameFilter) {
              matches =
                matches &&
                Boolean(user.surname) &&
                user.surname.toLowerCase().includes(surnameFilter);
            }

            if (emailFilter) {
              matches =
                matches &&
                Boolean(user.email) &&
                user.email.toLowerCase().includes(emailFilter);
            }

            if (uidFilter) {
              matches =
                matches &&
                Boolean(user.uid || user.uid === '') &&
                user.uid?.toLowerCase().includes(uidFilter);
            }

            return matches;
          })
          .sort((a, b) => {
            const surnameA = (a.surname || '').toLowerCase();
            const surnameB = (b.surname || '').toLowerCase();
            if (surnameA === surnameB) {
              return (a.name || '').localeCompare(b.name || '');
            }
            return surnameA.localeCompare(surnameB);
          });

        users.value = results;
        if (results.length === 0) {
          searchError.value = 'Nessun utente trovato con i criteri indicati.';
        }
      } catch (error) {
        console.error('Errore durante la ricerca utenti:', error);
        searchError.value = 'Impossibile completare la ricerca. Riprovare.';
      } finally {
        isSearching.value = false;
      }
    };

    const resetSearch = () => {
      selectionGeneration += 1;
      filters.surname = '';
      filters.email = '';
      filters.uid = '';
      users.value = [];
      hasSearched.value = false;
      searchError.value = '';
      selectedPrimary.value = null;
      selectedSecondary.value = null;
      resetPreviewState();
    };

    const initializeFinalUser = () => {
      Object.keys(finalUser).forEach((key) => {
        delete finalUser[key];
      });

      displayedFields.value.forEach((field) => {
        const value = previewData.value.primaryUser[field.key];
        finalUser[field.key] = normalizeForModel(value, field);
      });

      mergeOptions.includeSecondaryEmail =
        !!previewData.value.secondaryUser.email &&
        previewData.value.secondaryUser.email !== previewData.value.primaryUser.email;
      mergeOptions.additionalAliases = '';
      mergeOptions.note = '';
      mergeResult.value = null;
    };

    const loadPreview = async () => {
      const primaryUid = selectedPrimary.value;
      const secondaryUid = selectedSecondary.value;
      if (!primaryUid || !secondaryUid) {
        const preserveResult = !!mergeResult.value && primaryUid === mergeResult.value.primaryUid && !secondaryUid;
        resetPreviewState({ preserveResult });
        return;
      }

      const request = ++previewGeneration;
      const selection = selectionGeneration;
      const isCurrentPreview = () => request === previewGeneration && selection === selectionGeneration
        && selectedPrimary.value === primaryUid && selectedSecondary.value === secondaryUid;
      previewLoading.value = true;
      previewError.value = '';
      previewData.value = null;
      mergeResult.value = null;

      try {
        const response = await previewFn({ primaryUid, secondaryUid });
        if (!isCurrentPreview()) return;
        previewData.value = response.data;
        await nextTick();
        if (!isCurrentPreview()) return;
        initializeFinalUser();
      } catch (error) {
        console.error('Errore durante il caricamento della preview merge:', error);
        if (isCurrentPreview()) {
          previewError.value = error.message || 'Impossibile recuperare i dettagli degli utenti selezionati.';
        }
      } finally {
        if (request === previewGeneration) previewLoading.value = false;
      }
    };

    const applyField = (source, fieldKey) => {
      if (!previewData.value) return;
      const fieldMeta = displayedFieldMap.value.get(fieldKey);
      if (!fieldMeta) return;
      const sourceData =
        source === 'primary'
          ? previewData.value.primaryUser
          : previewData.value.secondaryUser;

      if (!(fieldKey in sourceData)) return;

      const value = sourceData[fieldKey];
      finalUser[fieldKey] = normalizeForModel(value, fieldMeta);
    };

    const applyAll = (source, { onlyEmpty = false } = {}) => {
      if (!previewData.value) return;
      displayedFields.value.forEach((field) => {
        if (onlyEmpty && !isEmptyValue(finalUser[field.key])) {
          return;
        }
        applyField(source, field.key);
      });
    };

    const fillMissingFromSecondary = () => {
      applyAll('secondary', { onlyEmpty: true });
    };

    const combineNotes = (fieldKey) => {
      if (!previewData.value) return;
      const fieldMeta = displayedFieldMap.value.get(fieldKey);
      if (!fieldMeta?.isNote) return;

      const primaryValue = previewData.value.primaryUser[fieldKey];
      const secondaryValue = previewData.value.secondaryUser[fieldKey];

      const parts = [];
      if (!isEmptyValue(primaryValue)) {
        parts.push(String(primaryValue).trim());
      }
      if (!isEmptyValue(secondaryValue)) {
        parts.push(String(secondaryValue).trim());
      }

      if (parts.length === 0) {
        finalUser[fieldKey] = '';
        return;
      }

      finalUser[fieldKey] = parts.join('\n\n');
    };

    const executeMerge = async () => {
      if (isMerging.value) return;
      mergeResult.value = null;
      if (!previewData.value) {
        previewError.value = 'Nessun dato da fondere.';
        return;
      }
      if (previewData.value.primaryUser.uid !== selectedPrimary.value
        || previewData.value.secondaryUser.uid !== selectedSecondary.value) {
        previewError.value = 'I profili selezionati sono cambiati. Attendi la nuova anteprima prima di unirli.';
        return;
      }

      if (
        !window.confirm(
          'Confermi di voler unire definitivamente gli account selezionati? L’operazione è irreversibile.'
        )
      ) {
        return;
      }

      isMerging.value = true;
      mergeResult.value = null;
      previewError.value = '';

      const finalEntries = Object.entries(finalUser).map(([key, value]) => {
        const fieldMeta = displayedFieldMap.value.get(key);
        if (fieldMeta?.component === 'select') {
          if (value === 'true') return [key, true];
          if (value === 'false') return [key, false];
        }
        return [key, value];
      });

      const payload = {
        primaryUid: previewData.value.primaryUser.uid,
        secondaryUid: previewData.value.secondaryUser.uid,
        finalUserData: Object.fromEntries(finalEntries),
        options: {
          includeSecondaryEmail: mergeOptions.includeSecondaryEmail,
          disableSecondaryAuth: mergeOptions.disableSecondaryAuth,
          additionalAliases: parseAliasList(mergeOptions.additionalAliases),
          note: mergeOptions.note,
        },
      };
      const selection = selectionGeneration;
      const isCurrentMerge = () => selection === selectionGeneration
        && selectedPrimary.value === payload.primaryUid && selectedSecondary.value === payload.secondaryUid;

      const clearedFields = displayedFields.value.filter((field) => {
        const finalValue = payload.finalUserData[field.key];
        const primaryValue = previewData.value.primaryUser[field.key];
        const secondaryValue = previewData.value.secondaryUser[field.key];
        return (
          isEmptyValue(finalValue) &&
          (!isEmptyValue(primaryValue) || !isEmptyValue(secondaryValue))
        );
      });

      if (
        clearedFields.length &&
        !window.confirm(
          `Stai lasciando vuoti i seguenti campi: ${clearedFields
            .map((field) => field.label)
            .join(', ')}.\nI dati originali non saranno più visibili dopo la fusione. Vuoi procedere comunque?`
        )
      ) {
        isMerging.value = false;
        return;
      }

      try {
        const response = await mergeFn(payload);
        if (!isCurrentMerge()) return response.data;
        mergeResult.value = response.data;
        // Rimuovi l’utente secondario dalla lista risultati
        users.value = users.value.filter(
          (user) => user.uid !== payload.secondaryUid
        );
        selectedSecondary.value = null;
        resetPreviewState({ preserveResult: true });
        return response.data;
      } catch (error) {
        console.error('Errore durante la fusione utenti:', error);
        if (isCurrentMerge()) {
          previewError.value = error.message || 'Impossibile completare la fusione degli account.';
        }
      } finally {
        isMerging.value = false;
      }
    };

    watch(
      () => [selectedPrimary.value, selectedSecondary.value],
      () => {
        selectionGeneration += 1;
        const preserveResult = !!mergeResult.value && selectedPrimary.value === mergeResult.value.primaryUid
          && !selectedSecondary.value;
        if (!preserveResult) mergeResult.value = null;
        if (selectedPrimary.value && selectedSecondary.value) {
          loadPreview();
        } else {
          resetPreviewState({ preserveResult });
        }
      },
      { flush: 'sync' }
    );

    return {
      filters,
      users,
      isSearching,
      hasSearched,
      searchError,
      selectedPrimary,
      selectedSecondary,
      previewLoading,
      previewError,
      previewData,
      displayedFields,
      finalUser,
      mergeOptions,
      mergeResult,
      isMerging,
      showPreviewPanel,
      referenceBreakdown,
      searchUsers,
      resetSearch,
      applyField,
      applyAll,
      fillMissingFromSecondary,
      combineNotes,
      executeMerge,
      formatDate,
      formatValue,
    };
  },
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date =
      typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('it-IT');
  } catch (error) {
    return '—';
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  if (typeof value.toDate === 'function') {
    return formatDate(value);
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};
</script>

<style scoped>
.merge-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page-header h1 {
  margin-bottom: 8px;
  font-size: 28px;
  font-weight: 700;
}

.page-intro {
  color: #4b5563;
  max-width: 880px;
  line-height: 1.5;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 16px 40px -20px rgba(15, 23, 42, 0.25);
  border: 1px solid rgba(15, 23, 42, 0.06);
  width: 100%;
  box-sizing: border-box;
}

.search-card {
  max-height: 200px;
  width: 97%;
}

.results-card {
  min-height: 200px;
  height: auto;
    width: 97%;
}

.merge-card {
  min-height: 240px;
  height: auto;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.card-header h2 {
  font-size: 20px;
  font-weight: 600;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  align-items: flex-end;
}

.search-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-weight: 600;
  color: #334155;
}

.search-field input {
  border-radius: 10px;
  border: 1px solid #d0d5dd;
  padding: 12px 14px;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-field input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.search-actions {
  display: flex;
  gap: 10px;
}

.search-hint {
  margin-top: 14px;
  color: #6b7280;
  font-size: 14px;
}

.inline-loader {
  font-size: 14px;
  color: #2563eb;
  display: flex;
  align-items: center;
  gap: 6px;
}

.empty-state {
  padding: 18px;
  background: #f8fafc;
  border-radius: 12px;
  color: #475569;
  text-align: center;
}

.table-wrapper {
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table th,
.users-table td {
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 10px;
  text-align: left;
  font-size: 14px;
  line-height: 1.4;
}

.users-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #1e293b;
}

.users-table tr:hover td {
  background: rgba(37, 99, 235, 0.04);
}

.radio-cell {
  width: 56px;
  text-align: center;
}

.uid-label {
  display: inline-block;
  font-family: 'Fira Code', monospace;
  font-size: 13px;
  color: #334155;
}

.merge-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) 280px;
  gap: 18px;
  margin-bottom: 24px;
  width: 100%;
  box-sizing: border-box;
}

.user-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.user-card--secondary {
  background: #fff9f0;
  border-color: #fed7aa;
}

.user-card__name {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.user-card dl {
  display: grid;
  gap: 8px;
}

.user-card dt {
  font-size: 13px;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.04em;
}

.user-card dd {
  margin: 0;
  font-size: 14px;
  color: #1f2937;
}

.references-card {
  border-radius: 14px;
  background: #0f172a;
  color: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.references-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.references-card__collection {
  font-weight: 600;
}

.references-card__count {
  font-family: 'Fira Code', monospace;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.12);
  padding: 2px 6px;
  border-radius: 999px;
  margin-left: 8px;
}

.references-card__note {
  margin-top: auto;
  font-size: 13px;
  opacity: 0.8;
}

.merge-table-wrapper {
  overflow-x: auto;
  width: 100%;
  box-sizing: border-box;
}

.merge-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  table-layout: fixed;
}

.merge-table th,
.merge-table td {
  border: 1px solid #e2e8f0;
  padding: 12px;
  vertical-align: top;
  word-wrap: break-word;
}

.merge-table th {
  background: #f1f5f9;
  font-weight: 600;
  color: #0f172a;
}

.value-col {
  width: 26%;
}

.value-cell {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  overflow: hidden;
}

.merge-row--note td {
  background: #fff7ed;
}

.final-col {
  position: relative;
}

.final-col--note {
  background: #fff7ed;
}

.note-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.final-input {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #d0d5dd;
  padding: 10px;
  font-size: 14px;
  min-height: 42px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.final-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.final-input--note {
  min-height: 120px;
  font-family: inherit;
}

.note-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #9a3412;
}

.merge-options {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
}

.option-line {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 14px;
  color: #1f2937;
}

.option-line--stacked {
  flex-direction: column;
  align-items: flex-start;
}

.option-line input[type='text'],
.option-line textarea {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #cbd5f5;
  padding: 10px 12px;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.option-line textarea {
  resize: vertical;
}

.merge-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  align-items: center;
}

.merge-actions__group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.result-card {
  margin-top: 24px;
  padding: 18px 20px;
  background: #f1f5f9;
  border-radius: 14px;
  border: 1px solid #cbd5f5;
  color: #0f172a;
}

.result-card h3 {
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 600;
}

.result-card ul {
  margin: 0 0 10px;
  padding-left: 20px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.2s ease, color 0.2s ease;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  box-shadow: none;
  transform: none;
}

.btn-primary {
  background: #2563eb;
  color: #ffffff;
  box-shadow: 0 10px 20px -10px rgba(37, 99, 235, 0.6);
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.btn-ghost {
  background: transparent;
  color: #1f2937;
  border: 1px solid #d0d5dd;
}

.btn-ghost:hover:not(:disabled) {
  border-color: #2563eb;
  color: #2563eb;
  transform: translateY(-1px);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  box-shadow: 0 10px 20px -12px rgba(239, 68, 68, 0.65);
}

.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.alert {
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
}

.alert-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.alert-info {
  background: #e0f2fe;
  color: #075985;
  border: 1px solid #bae6fd;
}

@media (max-width: 1200px) {
  .merge-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .references-card {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .references-card ul {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px 16px;
  }
}

@media (max-width: 960px) {
  .search-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .merge-summary {
    grid-template-columns: 1fr;
  }

  .references-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .merge-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .merge-actions__group {
    width: 100%;
    flex-direction: column;
  }

  .btn {
    justify-content: center;
  }
}
</style>
